/**
 * Gemensamt kontrakt för barnvyns fyra tidsvisare
 * (ring, stapel, klocka, digital).
 *
 * VIKTIGT: renderarna är RENA — de får den återstående tiden som prop och räknar
 * aldrig själva. Nedräkningen ägs av den delade useTimerState-hooken i växeln
 * (TimerCircle); ingen renderare får skapa en egen setInterval. Det är också det
 * som gör att ett byte av visningsform mitt i en pågående aktivitet inte kan
 * nollställa räkningen: hooken sitter kvar i växeln medan renderaren byts ut.
 *
 * Alla fyra visar SAMMA värde (återstående tid) och är därför utbytbara.
 */

/** Ytan varje renderare ska hålla sig inom (px) — ringens mått i dag. */
export const TIMER_SURFACE = 180;

export type TimerViewProps = {
  /** Återstående tid i sekunder, från den delade timerstaten. */
  time: number;
  /** Aktivitetens hela längd i sekunder (alltid minst 1). */
  totalSeconds: number;
  /** Etikett under tiden (starta/pågår/pausad/klart) — även aria-label. */
  label: string;
  /** Accentfärg, ett CSS-färgvärde ur temat. */
  color: string;
  /** Tap på ytan (starta/pausa). Utan den är ytan inte klickbar. */
  onToggle?: () => void;
  /**
   * Om mm:ss skrivs ut. Formen och siffrorna är två skilda val som handledaren
   * kombinerar fritt, så varje renderare måste kunna rita sig SJÄLV utan siffror —
   * etiketten (starta/pågår/pausad) står kvar oavsett, den är inte en klocka.
   */
  showDigits?: boolean;
};

/** mm:ss, nollutfyllt — samma format i alla former. */
export const formatTime = (totalSeconds: number): string => {
  const m = Math.floor(totalSeconds / 60);
  const s = totalSeconds % 60;
  return `${m.toString().padStart(2, '0')}:${s.toString().padStart(2, '0')}`;
};

/** Andel av tiden som är KVAR, klampad till 0–1. */
export const remainingFraction = (time: number, totalSeconds: number): number =>
  Math.min(Math.max(time / Math.max(totalSeconds, 1), 0), 1);

/** Andel av tiden som har GÅTT, klampad till 0–1. */
export const elapsedFraction = (time: number, totalSeconds: number): number =>
  1 - remainingFraction(time, totalSeconds);
